/**
 * DesktopSidebar - Sidebar navigation for desktop view
 * 
 * Only visible on screens >= 1024px (lg breakpoint)
 * Provides full navigation with icons and labels
 */

import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppContext, View } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { hasPermission } from '../../utils/permissions';
import { triggerHaptic } from '../../utils/haptics';
import { PrivacyBadge } from '../ui/PrivacyBadge';
import { usePendingJoinRequestsCount } from '../../hooks/usePendingJoinRequestsCount';
import { useGroupChatUnreadCount } from '../../hooks/useGroupChat';
import { useMatchmakingBadges } from '../../hooks/useMatchmakingBadges';
import { useTranslation } from 'react-i18next';

interface NavItem {
    icon: string;
    label: string;
    path: string;
    view?: View;
    badge?: number;
    isAdmin?: boolean;
    exactMatch?: boolean;
}

export const DesktopSidebar: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { currentGroup, db } = useAppContext();
    const { user, userData } = useAuth();

    const isAppAdmin = userData?.appRole === 'owner' || userData?.appRole === 'app_admin';

    // ─── Real Player Count Calculation ───
    // Filter out ghost players (not in /users) and duplicates, matching Players.tsx logic
    const realPlayerCount = React.useMemo(() => {
        if (!currentGroup?.players) return 0;
        const groupPlayers = currentGroup.players;
        const usersArray = db?.users || [];

        // 1. Get unique player IDs present in this group
        const uniqueIds = Array.from(new Set(groupPlayers.map(p => p.id)));

        // 2. Filter against global users collection if available
        if (usersArray.length > 0) {
            const validUserIds = new Set(usersArray.map((u: any) => u.id));
            return uniqueIds.filter(id => validUserIds.has(id)).length;
        }

        return uniqueIds.length;
    }, [currentGroup?.players, db?.users]);

    const canManageGroup = React.useMemo(() => {
        if (!user || !currentGroup) return false;
        return hasPermission(user, 'manage_group_settings', { group: currentGroup, globalConfig: db.globalConfig }) ||
            hasPermission(user, 'manage_group_members', { group: currentGroup, globalConfig: db.globalConfig });
    }, [user, currentGroup, db.globalConfig]);

    // Count unread notifications
    const unreadCount = db.notifications?.filter(n => n.playerId === user?.uid && !n.read).length || 0;

    // Count pending join requests for groups requiring approval
    const { count: pendingJoinRequestsCount } = usePendingJoinRequestsCount(
        currentGroup?.privacyMode === 'request' ? currentGroup?.id : undefined
    );

    // Count unread chat messages
    const chatUnreadCount = useGroupChatUnreadCount(currentGroup?.id);

    // Count actionable matchmaking items
    const { totalBadges: matchmakingUnreadCount } = useMatchmakingBadges(currentGroup, user?.uid);

    // ─── Global nav items (always visible) ───
    const globalNavItems: NavItem[] = [
        { icon: 'home', label: t('sidebar.home', 'Accueil'), path: '/', view: View.HUB, exactMatch: true },
        { icon: 'dashboard', label: t('sidebar.dashboard', 'Tableau de Bord'), path: '/dashboard', view: View.DASHBOARD, exactMatch: true },
        { icon: 'folder_shared', label: t('sidebar.groups', 'Les Groupes'), path: '/groups', view: View.GROUP_SELECT, exactMatch: true },
        { icon: 'swords', label: t('sidebar.challenges', 'Défis & Matchs'), path: '/challenges', view: View.CHALLENGES },
        { icon: 'scoreboard', label: t('sidebar.score', 'Arbitrage'), path: '/score', view: ('SCORE' as View) },
        { icon: 'location_city', label: t('sidebar.clubs', 'Clubs'), path: '/clubs', view: View.CLUBS },
    ];

    // ─── Group-specific nav items ───
    const groupNavItems: NavItem[] = currentGroup ? [
        { icon: 'emoji_events', label: t('sidebar.tournaments', 'Tournois'), path: `/group/${currentGroup.id}/tournaments`, view: View.TOURNAMENTS },
        { icon: 'handshake', label: t('sidebar.matchmaking', 'MatchMaking'), path: `/group/${currentGroup.id}/matchmaking`, view: View.MATCHMAKING, badge: matchmakingUnreadCount > 0 ? matchmakingUnreadCount : undefined },
        { icon: 'groups', label: t('sidebar.ranking', 'Classement'), path: `/group/${currentGroup.id}/players`, view: View.PLAYERS },
        { icon: 'forum', label: t('sidebar.chat', 'Chat'), path: `/group/${currentGroup.id}/chat`, view: View.GROUP_CHAT, badge: chatUnreadCount > 0 ? chatUnreadCount : undefined },
    ] : [];


    // ─── Social nav items ───
    const socialNavItems: NavItem[] = [
        { icon: 'people', label: t('sidebar.friends', 'Amis'), path: '/friends', view: View.FRIENDS },
        { icon: 'inbox', label: t('sidebar.inbox', 'Boîte de réception'), path: '/inbox', view: View.INBOX, badge: unreadCount },
    ];

    const adminNavItems: NavItem[] = [];
    if (canManageGroup && currentGroup) {
        adminNavItems.push({
            icon: 'admin_panel_settings',
            label: t('sidebar.groupAdmin', 'Admin Groupe'),
            path: `/group/${currentGroup.id}/admin`,
            view: View.GROUP_ADMIN,
            badge: pendingJoinRequestsCount > 0 ? pendingJoinRequestsCount : undefined
        });
    }
    if (isAppAdmin) {
        adminNavItems.push({
            icon: 'shield_person',
            label: t('sidebar.admin', 'Administration'),
            path: '/admin',
            view: View.ADMIN,
            isAdmin: true
        });
    }

    const handleNavClick = (item: NavItem) => {
        triggerHaptic();
        navigate(item.path);
    };

    const isActive = (item: NavItem) => {
        if (item.exactMatch) {
            return location.pathname === item.path;
        }
        if (item.path.includes('/group/') && item.path.includes('/')) {
            return location.pathname === item.path;
        }
        if (item.path === '/') {
            return location.pathname === '/';
        }
        return location.pathname === item.path || location.pathname.startsWith(item.path + '/');
    };

    // ─── Render a single nav button ───
    const renderNavButton = (item: NavItem) => (
        <button
            key={item.path}
            onClick={() => handleNavClick(item)}
            className={`w-full flex items-center gap-2.5 px-3 py-1.5 rounded-lg transition-all duration-200 group ${isActive(item)
                ? 'bg-primary text-white shadow-lg shadow-primary/25'
                : 'text-text-secondary hover:text-text-primary hover:bg-surface-2'
                }`}
        >
            <span className={`material-symbols-outlined text-lg ${isActive(item) ? 'text-white' : 'group-hover:text-primary'
                }`}>
                {item.icon}
            </span>
            <span className="font-medium text-sm flex-1 text-left">{item.label}</span>
            {item.badge && item.badge > 0 && (
                <span className="bg-danger text-white text-xs font-bold px-2 py-0.5 rounded-full min-w-[20px] text-center">
                    {item.badge > 99 ? '99+' : item.badge}
                </span>
            )}
        </button>
    );

    // Get user's profile picture
    const userProfilePicture = userData?.photoURL || userData?.avatar;
    const isUrl = userProfilePicture && (userProfilePicture.startsWith('http') || userProfilePicture.startsWith('data:image') || userProfilePicture.startsWith('/'));

    return (
        <aside className="hidden lg:flex flex-col w-60 h-screen bg-surface-1 border-r border-glass-border sticky top-0 z-50 overflow-y-auto flex-shrink-0">
            {/* Logo / Brand */}
            <div className="py-1 border-b border-glass-border flex justify-center overflow-hidden">
                <img
                    src="/app_title_logo.webp"
                    alt="Padel Brothers"
                    className="h-14 object-contain drop-shadow-md"
                />
            </div>

            {/* Current Group Selector */}
            {currentGroup && (
                <div className="px-3 py-2 border-b border-glass-border">
                    <button
                        onClick={() => navigate('/groups')}
                        className="w-full flex items-center gap-2.5 p-2 min-h-[40px] rounded-lg bg-surface-2 hover:bg-surface-3 transition-colors"
                    >
                        {currentGroup.logoUrl ? (
                            <img src={currentGroup.logoUrl} alt="" className="w-8 h-8 rounded-lg object-cover" />
                        ) : (
                            <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center">
                                <span className="text-primary text-sm font-bold">{currentGroup.name[0]}</span>
                            </div>
                        )}
                        <div className="flex-1 text-left min-w-0">
                            <div className="flex items-center gap-2">
                                <p className="text-sm font-semibold text-text-primary truncate">{currentGroup.name}</p>
                                {currentGroup.id !== 'official_padel_brothers' && (
                                    <PrivacyBadge
                                        mode={currentGroup.privacyMode}
                                        compact
                                        className="w-4 h-4 text-[10px]"
                                        isMember={(currentGroup.players || []).some(p => p.id === user?.uid) || (currentGroup.members || []).includes(user?.uid || '')}
                                    />
                                )}
                            </div>
                            <p className="text-xs text-text-secondary">{realPlayerCount} {realPlayerCount > 1 ? t('profile.players', 'joueurs') : t('matchmaking.player', 'joueur')}</p>
                        </div>
                        <span className="material-symbols-outlined text-text-secondary text-sm flex-shrink-0">unfold_more</span>
                    </button>

                    {/* Group-specific nav items — visually nested under the group */}
                    {groupNavItems.length > 0 && (
                        <div className="mt-2 bg-surface-2/50 rounded-xl border border-white/5 p-1.5 space-y-0.5">
                            {groupNavItems.map(item => (
                                <button
                                    key={item.path}
                                    onClick={() => handleNavClick(item)}
                                    className={`w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg transition-all duration-200 group ${isActive(item)
                                        ? 'bg-primary text-white shadow-md shadow-primary/25'
                                        : 'text-text-primary hover:bg-surface-3'
                                        }`}
                                >
                                    <span className={`material-symbols-outlined text-lg ${isActive(item) ? 'text-white' : 'text-primary'
                                        }`}>
                                        {item.icon}
                                    </span>
                                    <span className="font-semibold text-sm">{item.label}</span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Navigation */}
            <nav className="flex-1 overflow-y-auto px-3 pt-2 pb-1">
                {/* Global Navigation */}
                <div className="mb-2">
                    <div className="space-y-1">
                        {globalNavItems.map(renderNavButton)}
                    </div>
                </div>

                {/* Social Section */}
                <div className="mb-2">
                    <p className="text-[10px] font-bold text-text-secondary/50 uppercase tracking-[0.15em] mb-1 px-3">{t('sidebar.sectionSocial', 'Social')}</p>
                    <div className="space-y-1">
                        {socialNavItems.map(renderNavButton)}
                    </div>
                </div>

                {/* Admin Section */}
                {adminNavItems.length > 0 && (
                    <div className="mb-2">
                        <p className="text-[10px] font-bold text-text-secondary/50 uppercase tracking-[0.15em] mb-1 px-3">{t('sidebar.sectionAdmin', 'Administration')}</p>
                        <div className="space-y-1">
                            {adminNavItems.map(renderNavButton)}
                        </div>
                    </div>
                )}
            </nav>

            {/* Legal Information */}
            <div className="px-3 pb-2">
                <button
                    onClick={() => navigate('/legal')}
                    className="w-full flex items-center justify-between px-3 py-1.5 min-h-[36px] rounded-lg text-text-secondary hover:text-text-primary hover:bg-surface-2 transition-colors"
                >
                    <div className="flex items-center gap-3">
                        <span className="material-symbols-outlined text-lg">gavel</span>
                        <span className="text-xs font-bold uppercase tracking-wider">{t('menu.legal_info', 'Informations Légales')}</span>
                    </div>
                </button>
                <div className="mt-1 px-3 flex flex-col gap-0.5">
                    <p className="text-[10px] text-text-secondary/50 uppercase">© {new Date().getFullYear()} Padel Brothers By Pro Art</p>
                </div>
            </div>

            {/* User Profile / Login */}
            <div className="p-3 border-t border-glass-border">
                {user ? (
                    <button
                        onClick={() => navigate('/profile')}
                        className="w-full flex items-center gap-2.5 p-2 min-h-[40px] rounded-xl hover:bg-surface-2 transition-colors"
                    >
                        {isUrl ? (
                            <img
                                src={userProfilePicture}
                                alt=""
                                className="w-8 h-8 rounded-full object-cover border-2 border-primary/30"
                            />
                        ) : (
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center border-2 border-primary/30">
                                {userData?.avatar && userData.avatar.length <= 4 ? (
                                    <span className="text-lg">{userData.avatar}</span>
                                ) : (
                                    <span className="text-white font-bold text-sm">
                                        {userData?.pseudo?.[0]?.toUpperCase() || userData?.name?.[0]?.toUpperCase() || 'U'}
                                    </span>
                                )}
                            </div>
                        )}
                        <div className="flex-1 text-left min-w-0">
                            <p className="text-sm font-semibold text-text-primary truncate">{t('sidebar.myProfile', 'Mon Profil')}</p>
                            <p className="text-xs text-text-secondary truncate">@{userData?.pseudo || t('sidebar.user', 'utilisateur')}</p>
                        </div>
                        <span className="material-symbols-outlined text-text-secondary flex-shrink-0">settings</span>
                    </button>
                ) : (
                    <button
                        onClick={() => navigate('/login')}
                        className="w-full flex items-center gap-2.5 p-3 rounded-xl bg-gradient-to-r from-primary to-secondary text-white font-bold hover:brightness-110 active:scale-95 transition-all shadow-lg"
                    >
                        <span className="material-symbols-outlined">login</span>
                        <span>{t('login.signIn', 'Connexion')}</span>
                    </button>
                )}
            </div>
        </aside>
    );
};

